import React from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CTASection } from "@/components/home/CTASection";
import { Lightbulb, MagnifyingGlass, Package, Truck, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const STEPS = [
  { icon: Lightbulb, title: "1. Product & Niche Validation", desc: "We help you validate demand and select a product with healthy margins and manageable competition." },
  { icon: MagnifyingGlass, title: "2. OEM/ODM Factory Match", desc: "We find manufacturers capable of customizing your product and meeting realistic MOQs." },
  { icon: Package, title: "3. Branding & Packaging", desc: "Custom logos, retail-ready packaging and inserts designed to build instant trust on the shelf." },
  { icon: CheckCircle, title: "4. Sampling & QC", desc: "Low-MOQ pilot runs with rigorous AQL inspection before you commit to scale." },
  { icon: Truck, title: "5. FBA Prep & Launch", desc: "Labeling, prep and direct shipping into Amazon fulfillment centers — fully compliant." },
];

export default function StartYourBrand() {
  return (
    <div data-testid="start-brand-page">
      <PageHero
        overline="Start your brand"
        title="Build a private label & Amazon FBA brand from China"
        subtitle="Go from product idea to live, branded inventory in 8–12 weeks — with a dedicated brand manager guiding every step."
        crumbs={[{ label: "Start Your Brand" }]}
        image="https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg"
      />

      <section className="bg-white py-24 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="space-y-5">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.05}>
                <div className="flex items-start gap-5 rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:shadow-lg">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#1E73BE] to-[#16B894] text-white">
                    <s.icon size={24} weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-ink">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-ink p-10 text-center text-white">
            <h2 className="font-heading text-2xl font-semibold sm:text-3xl">Ready to launch your brand?</h2>
            <p className="mx-auto mt-3 max-w-xl text-slate-300">Book a free brand consultation and we'll map out your product roadmap, costs and timeline.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-4">
              <Link to="/contact?type=consultation" data-testid="brand-book-consult" className="flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1E73BE] to-[#16B894] px-7 py-3.5 text-sm font-semibold">
                Book Brand Consultation <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/contact?type=quote" className="rounded-md border border-white/30 px-7 py-3.5 text-sm font-semibold hover:bg-white/10">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
